import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import IndexedDb from '../../../../../Helpers/IndexedDb';
import { REACT_APP_CONTACT_SYNC } from '../../../../../Components/processENV';
import { getFormatPhoneNumber } from '../../../../../Helpers/Utility';

export const UserProfile = ({ userId, className, defaultIcon, isLocal = false }) => {
    const [imgError, setImgError] = useState(false);
    const [blobUrl, setBlobUrl] = useState(null);
    const users = useSelector(state => state.userInfoData?.users || {});
    const groups = useSelector(state => state.groupsData?.data || []);
    const rosterData = useSelector(state => state.rosterData?.data || []);

    // First look in userInfoData; if not found, try groupsData (for group calls)
    const user = users[userId] || {};
    const rosterUser = useMemo(() => {
        const id = userId ? userId.split('@')[0] : null;
        if (!id) return null;
        let matched = rosterData.find(r => r && (r.userId === id || r.userId === userId));
        if (!matched) {
            // Try matching using properties of the user object (if id is a UUID)
            const userPhone = (user.userId || user.username || user.mobileNumber || '').split('@')[0];
            if (userPhone && userPhone !== id) {
                matched = rosterData.find(r => {
                    if (!r) return false;
                    const rid = r.userId?.split('@')[0];
                    if (!rid || rid.length < 7 || userPhone.length < 7) return false;
                    return rid.endsWith(userPhone) || userPhone.endsWith(rid);
                });
            }
        }
        if (!matched) {
            // Fuzzy match for phone numbers (e.g. 919884753583 matches 9884753583)
            matched = rosterData.find(r => {
                if (!r) return false;
                const rid = r.userId?.split('@')[0];
                const isMatch =
                    rid && id && rid.length >= 7 && id.length >= 7 && (rid.endsWith(id) || id.endsWith(rid));
                return isMatch;
            });
        }
        return matched;
    }, [rosterData, userId, user]);

    const isFriend = user.isFriend || rosterUser?.isFriend;
    const isDeletedUser = user.isDeletedUser || rosterUser?.isDeletedUser;

    const rawGroupId = userId ? userId.split('@')[0] : null;
    const matchedGroup = groups.find(g => g && (g.groupId === rawGroupId || g.groupId === userId));

    const image =
        user.thumbImage ||
        user.image ||
        user.profileImage ||
        matchedGroup?.thumbImage ||
        matchedGroup?.groupImage ||
        null;

    const localDb = useMemo(() => new IndexedDb(), []);
    const isMountedRef = useRef(true);

    const blobUrlRef = useRef(null);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (blobUrlRef.current) {
                window.URL.revokeObjectURL(blobUrlRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!image) {
            setBlobUrl(null);
            return;
        }

        if (/^(blob:http|blob:https):\/\//i.test(image)) {
            setBlobUrl(image);
            return;
        }

        localDb
            .getImageByKey(image, 'profileimages', '')
            .then(blob => {
                if (isMountedRef.current) {
                    if (blobUrlRef.current) {
                        window.URL.revokeObjectURL(blobUrlRef.current);
                    }
                    const url = window.URL.createObjectURL(blob);
                    blobUrlRef.current = url;
                    setBlobUrl(url);
                    setImgError(false);
                }
            })
            .catch(() => {
                if (isMountedRef.current) {
                    setBlobUrl(null);
                    setImgError(true);
                }
            });
    }, [image, localDb]);

    if (blobUrl && !imgError) {
        return (
            <div className={className}>
                <img
                    src={blobUrl}
                    alt={userId}
                    onError={() => setImgError(true)}
                    style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                />
            </div>
        );
    }

    return (
        <div className={className}>
            {defaultIcon || (
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        backgroundColor: user.colorCode || '#6A7BFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: 'inherit',
                        fontWeight: '600',
                    }}
                >
                    {(() => {
                        const isPhone = !!(REACT_APP_CONTACT_SYNC && !isFriend && !isDeletedUser);
                        let nameToUse = '';
                        if (isLocal) {
                            nameToUse = 'You';
                        } else if (isPhone) {
                            nameToUse = getFormatPhoneNumber(userId) || userId || '';
                        } else {
                            nameToUse =
                                rosterUser?.name ||
                                rosterUser?.displayName ||
                                rosterUser?.nickName ||
                                user.nickName ||
                                user.name ||
                                userId ||
                                '';
                        }

                        if (isLocal) return 'Y';
                        if (isPhone) {
                            // Strip country code: handles +91..., 91..., or just +...
                            const stripped = nameToUse.replace(/^(\+\d{1,3}\s?|\d{2}\s?(?=\d{10}))/, '');
                            return stripped.charAt(0).toUpperCase();
                        }
                        return nameToUse.charAt(0).toUpperCase();
                    })()}
                </div>
            )}
        </div>
    );
};

export const Username = ({ userId, maxLength = 20, isLocal = false, name: propName }) => {
    const users = useSelector(state => state.userInfoData?.users || {});
    const groups = useSelector(state => state.groupsData?.data || []);
    const rosterData = useSelector(state => state.rosterData?.data || []);
    const user = users[userId] || {};

    const rosterUser = useMemo(() => {
        const id = userId ? userId.split('@')[0] : null;
        if (!id) return null;
        let matched = rosterData.find(r => r && (r.userId === id || r.userId === userId));
        if (!matched) {
            // Try matching using properties of the user object (if id is a UUID)
            const userPhone = (user.userId || user.username || user.mobileNumber || '').split('@')[0];
            if (userPhone && userPhone !== id) {
                matched = rosterData.find(r => {
                    if (!r) return false;
                    const rid = r.userId?.split('@')[0];
                    if (!rid || rid.length < 7 || userPhone.length < 7) return false;
                    return rid.endsWith(userPhone) || userPhone.endsWith(rid);
                });
            }
        }
        if (!matched) {
            // Fuzzy match for phone numbers (e.g. 919884753583 matches 9884753583)
            matched = rosterData.find(r => {
                if (!r) return false;
                const rid = r.userId?.split('@')[0];
                const isMatch =
                    rid && id && rid.length >= 7 && id.length >= 7 && (rid.endsWith(id) || id.endsWith(rid));
                return isMatch;
            });
        }
        return matched;
    }, [rosterData, userId, user]);

    const isFriend = user.isFriend || rosterUser?.isFriend;
    const isDeletedUser = user.isDeletedUser || rosterUser?.isDeletedUser;

    // Resolve from userInfoData first, fall back to groupsData for group calls
    const rawGroupId = userId ? userId.split('@')[0] : null;
    const matchedGroup = !user.nickName
        ? groups.find(g => g && (g.groupId === rawGroupId || g.groupId === userId))
        : null;

    let name = '';
    const userIdentifier = userId || '';
    if (isLocal) {
        name = 'You';
    } else if (REACT_APP_CONTACT_SYNC && !isFriend && !isDeletedUser && !matchedGroup) {
        name = getFormatPhoneNumber(userIdentifier) || userIdentifier;
    } else {
        name =
            rosterUser?.name ||
            rosterUser?.displayName ||
            rosterUser?.nickName ||
            user.nickName ||
            user.name ||
            propName ||
            userId ||
            '';
    }
    const displayName = name.length > maxLength ? name.substring(0, maxLength) + '...' : name;

    return <span>{displayName}</span>;
};
